import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MODE_TINT } from "@/lib/api";
import { useApp } from "@/lib/store";
import * as flow from "@/lib/flow";
import { markCheckin } from "@/lib/reminders";
import { track } from "@/lib/analytics";
import { getActiveLens, clearActiveLens } from "@/lib/attribution";
import { getLens } from "@/lib/lenses";
import SafetyLink from "@/components/SafetyLink";import ProtocolRunner from "./ProtocolRunner";
import Feedback from "./Feedback";
import SupportSignalSheet from "./SupportSignalSheet";
import OperatingState from "./OperatingState";
import DriftRadar from "./DriftRadar";

export default function ResultView({ initial, onRestart }) {
  const { content, user, progress, refreshProgress, setFocusMode } = useApp();
  const navigate = useNavigate();
  const [result, setResult] = useState(initial);
  const [phase, setPhase] = useState("view"); // view | running | feedback | done
  const [accuracy, setAccuracy] = useState(null);
  const [showCorrections, setShowCorrections] = useState(false);
  const [presetAttempted, setPresetAttempted] = useState(null);
  const [showSupport, setShowSupport] = useState(false);

  // Focused flow: hide bottom nav until the moment is complete (done screen restores it).
  useEffect(() => { setFocusMode(phase !== "done"); }, [phase, setFocusMode]);
  useEffect(() => () => setFocusMode(false), [setFocusMode]);

  const tint = MODE_TINT[result.mode] || "#F5F3ED";
  const ctxObj = content?.contexts?.find((c) => c.id === result._ctxId) || null;
  const readout = result.readout || {};
  const stateBlock = result.state_block || {
    headline: readout.state, what_changed: readout.what_changed, direction: readout.direction,
  };
  const responseBlock = result.response_block || {
    mode_label: result.mode_label, mode_purpose: result.mode_purpose,
    recommended_action: readout.next, check_again: readout.check_again,
  };
  const activeLens = getLens(getActiveLens());

  const chooseAccuracy = async (rating) => {
    setAccuracy(rating);
    try { await flow.postAccuracy(result, rating); } catch (e) {}
    setShowCorrections(rating === "not_quite");
  };

  const applyCorrection = async (mode) => {
    try {
      const data = await flow.correctMode(result, mode, ctxObj);
      data._ctxId = result._ctxId; data._timeId = result._timeId;
      setResult(data);
      setAccuracy(null);
      setShowCorrections(false);
      toast("Response updated");
    } catch (e) { toast("Could not update"); }
  };

  const chooseSmaller = async () => {
    try {
      const data = await flow.getSmaller(result);
      setResult((r) => ({ ...r, protocol: data.protocol }));
      toast("Here is a smaller option");
    } catch (e) { toast("No smaller option available"); }
  };

  const completeFeedback = async (feedback) => {
    try { await flow.submitFeedback(result, feedback); } catch (e) {}
    track("checkin_completed", { mode: result.mode, stage: result.stage?.stage });
    clearActiveLens();
    markCheckin();
    refreshProgress();
    setPhase("done");
  };

  if (phase === "running") {
    return <ProtocolRunner protocol={result.protocol} onFinish={() => { setPresetAttempted(null); setPhase("feedback"); }} />;
  }

  if (phase === "feedback") {
    return (
      <Feedback
        reportedChangeOptions={result.reported_change_options}
        presetAttempted={presetAttempted}
        onComplete={completeFeedback}
      />
    );
  }

  if (phase === "done") {
    const pr = progress || {};
    const pct = pr.needed ? Math.min(100, Math.round((pr.count / pr.needed) * 100)) : 0;
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="px-6 pt-16 pb-28"
      >
        <h1 className="font-serif-display text-4xl leading-tight">That's one moment handled.</h1>
        <p className="mt-4 text-[15px] text-[#474747] leading-relaxed">
          There's nothing to keep up and no streak to protect. Come back whenever something feels
          off — as many times a day as you need.
        </p>

        {pr.needed ? (
          pr.unlocked ? (
            <div className="mt-8 ss-card p-5" data-testid="done-pattern-ready">
              <p className="text-[15px] font-medium">Your first Pattern Review is ready.</p>
              <p className="mt-1.5 text-[13.5px] text-[#474747]">
                See which states recur for you and what has actually helped.
              </p>
              <button
                data-testid="done-see-pattern"
                onClick={() => navigate("/pattern")}
                className="mt-4 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus hover:-translate-y-0.5"
              >
                See what my check-ins reveal
              </button>
            </div>
          ) : (
            <div className="mt-8 ss-card p-5" data-testid="done-progress">
              <p className="font-mono-ss text-sm text-[#6B6B6B]">
                {pr.count} of {pr.needed} check-ins toward your first Pattern Review
              </p>
              <div className="mt-3 h-[3px] w-full bg-[#EBE9E4] rounded-full overflow-hidden">
                <div className="h-full bg-[#C9A227] transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-3 text-[12.5px] text-[#6B6B6B]">
                Your progress never expires. Missed days don't reset it.
              </p>
            </div>
          )
        ) : null}

        <DriftRadar className="mt-6" />

        <div className="mt-8">
          <button
            data-testid="done-home"
            onClick={() => { navigate("/home"); onRestart(); }}
            className="w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus hover:-translate-y-0.5"
          >
            Go to home
          </button>
          <p className="mt-3 text-center text-[13px] text-[#9a9a94] leading-relaxed px-4">
            Come back later once you've had time to try what was suggested.
          </p>

          <div className="mt-7 pt-6 border-t border-[#EBE9E4] flex flex-col items-center gap-4">
            <button
              data-testid="done-check-again"
              onClick={() => { navigate(`/navigate?reassess=${result.checkin_id}`); onRestart(); }}
              className="text-[13.5px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors"
            >
              Review how I feel now
            </button>
            <button
              data-testid="recheck-btn"
              onClick={() => { navigate("/navigate", { replace: true }); onRestart(); }}
              className="text-[13.5px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors"
            >
              Start a new check-in
            </button>
          </div>
        </div>
        {!user && (
          <button
            data-testid="done-save-progress"
            onClick={() => navigate("/login")}
            className="mt-6 w-full text-center text-[14px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors py-2"
          >
            Save my progress across devices
          </button>
        )}
      </motion.div>
    );
  }

  // phase === "view"
  const p = result.protocol;
  return (
    <div className="pb-28">
      {/* ── YOUR STATE — how you're operating right now ── */}
      <div className="px-6 pt-10">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a6d10]">
          Your state
        </span>
        <h1 className="mt-2 font-serif-display text-4xl leading-tight" data-testid="result-state">
          {stateBlock.headline}
        </h1>
        {stateBlock.what_changed && (
          <p className="mt-3 text-[15px] text-[#474747] leading-relaxed">
            <span className="text-[#8a6d10]">What's shifted:</span> {stateBlock.what_changed}
          </p>
        )}
        {stateBlock.direction && (
          <div
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F1EFE8] px-3.5 py-1.5"
            data-testid="result-direction"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
            <span className="text-[12.5px] font-medium text-[#474747]">{stateBlock.direction}</span>
          </div>
        )}
      </div>

      {result.transition && (
        <div className="px-6 mt-4">
          <div className="ss-card p-4 border-l-2 border-[#7fa98c]" data-testid="result-transition">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6B6B6B]">Since your last check</span>
            <p className="mt-1.5 text-[14px] text-[#1A1A1A]">
              {result.transition.from_label} → <span className="font-medium">{result.transition.to_label}</span>
              {" · "}
              {{ eased: "that eased", similar: "about the same", heavier: "a heavier reading", shifted: "a different reading" }[result.transition.moved]}
            </p>
          </div>
        </div>
      )}

      {/* ── YOUR RESPONSE — the recommended way to respond ── */}
      <div className="px-6 mt-8">
        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">
          Your response
        </span>
        <div className="mt-2 inline-flex items-center gap-3">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#C9A227" }} />
          <h2 className="font-serif-display text-5xl" data-testid="result-mode">{responseBlock.mode_label}</h2>
        </div>
        <p className="mt-2 text-[15px] text-[#474747]">{responseBlock.mode_purpose}</p>
        {responseBlock.check_again && (
          <p className="mt-3 text-[13px] text-[#6B6B6B]" data-testid="result-check-again">
            <span className="font-semibold uppercase tracking-[0.1em] text-[#8a6d10] text-[11.5px]">Check again</span>
            {" · "}{responseBlock.check_again}
          </p>
        )}
        {result.learning && (
          <div className="mt-4 rounded-xl border border-[#EBE9E4] bg-[#FBFAF6] p-3.5" data-testid="result-learning">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
              What you've learned
            </div>
            <p className="mt-1 text-[13.5px] leading-relaxed text-[#474747]">{result.learning.line}</p>
          </div>
        )}
      </div>

      <div className="px-6 mt-6 space-y-4">
        {activeLens && (
          <div className="ss-card p-5 border-l-2 border-[#C9A227]" data-testid="result-lens-card">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">
              Through the {activeLens.name} lens
            </p>
            <div className="mt-3 space-y-2.5">
              {activeLens.prompts.map(([label, prompt], i) => (
                <div key={i} className="flex gap-3">
                  <span className="w-[120px] flex-shrink-0 pt-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B6B6B]">{label}</span>
                  <span className="flex-1 text-[14px] leading-snug text-[#1A1A1A]">{prompt}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 border-t border-[#EFEDE7] pt-3 text-[12.5px] text-[#9a9a94]">
              A lens shapes what to reflect on — it doesn't change your reading.
            </p>
          </div>
        )}
        <div className="ss-card p-5" style={{ backgroundColor: tint }}>
          <p className="text-[15.5px] leading-relaxed text-[#1A1A1A]">{result.observation}</p>
          {result.demand_line && (
            <p className="mt-3 text-[15px] leading-relaxed text-[#474747]">{result.demand_line}</p>
          )}
        </div>
        <p className="text-[14.5px] italic text-[#474747] leading-relaxed px-1">
          {result.validation}
        </p>
      </div>

      {/* Accuracy */}
      <div className="px-6 mt-6">
        <p className="text-[15px] font-medium mb-3">Does this feel accurate?</p>
        <div className="flex gap-3">
          {content.accuracy_options.map((a) => (
            <button
              key={a.id}
              data-testid={`accuracy-${a.id}`}
              onClick={() => chooseAccuracy(a.id)}
              className={`flex-1 rounded-full px-4 py-3 text-[14px] font-medium ss-tap ss-focus border ${
                accuracy === a.id
                  ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]"
                  : "border-[#EBE9E4] bg-white"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {showCorrections && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            className="mt-4 overflow-hidden"
          >
            <p className="text-[14px] text-[#474747] mb-3">Choose what fits better:</p>
            <div className="space-y-2">
              {content.corrections.map((c) => (
                <button
                  key={c.mode}
                  data-testid={`correct-${c.mode}`}
                  onClick={() => applyCorrection(c.mode)}
                  className="w-full text-left ss-card ss-tap ss-focus p-3.5 text-[14.5px]"
                >
                  {c.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Optional operating-state language (after accuracy, before protocol) */}
      {accuracy && !showCorrections && (
        <OperatingState result={result} content={content} />
      )}

      {/* Protocol */}
      <div className="px-6 mt-8">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">
          One thing to try next
        </span>
        <div className="mt-3 ss-card p-6" data-testid="protocol-card">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-serif-display text-2xl">{p.title}</h2>
            <span className="font-mono-ss text-xs text-[#C9A227] whitespace-nowrap">{p.duration_label}</span>
          </div>
          <p className="mt-2 text-[14.5px] text-[#474747] leading-relaxed">{p.purpose}</p>

          <button
            data-testid="protocol-start"
            onClick={() => setPhase("running")}
            className="mt-5 w-full bg-[#C9A227] text-[#1A1A1A] rounded-full px-6 py-3.5 font-semibold ss-tap ss-focus hover:-translate-y-0.5"
          >
            Start
          </button>
          <div className="mt-3 flex flex-col gap-2">
            <button
              data-testid="protocol-smaller"
              onClick={chooseSmaller}
              className="text-[13.5px] text-[#474747] hover:text-[#1A1A1A] ss-focus transition-colors"
            >
              Choose something smaller
            </button>
            <button
              data-testid="protocol-not-possible"
              onClick={() => { setPresetAttempted("no"); setPhase("feedback"); }}
              className="text-[13.5px] text-[#474747] hover:text-[#1A1A1A] ss-focus transition-colors"
            >
              This is not possible right now
            </button>
          </div>
        </div>
      </div>

      {/* Permission */}
      <div className="px-6 mt-8">
        <div className="border-l-2 border-[#C9A227] pl-4">
          <p className="text-[15px] italic text-[#1A1A1A] leading-relaxed">{result.permission}</p>
        </div>
      </div>

      {/* Support signal + safety */}
      <div className="px-6 mt-8 flex flex-col items-start gap-5">
        <button
          data-testid="support-open"
          onClick={() => setShowSupport(true)}
          className="w-full border border-[#1A1A1A] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus"
        >
          Help me communicate what I need
        </button>
        <SafetyLink />
      </div>

      {showSupport && (
        <SupportSignalSheet result={result} onClose={() => setShowSupport(false)} />
      )}
    </div>
  );
}
